import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { MapRef } from 'react-map-gl';

import { DEFAULT_PANEL_WIDTH, EXPANDED_PANEL_WIDTH, MIN_PANEL_WIDTH } from '../constants';
import { snapPanelWidth } from '../utils';

function createDragHandlers(setPanelWidth: Dispatch<SetStateAction<number>>) {
  let isDragging = false;
  let dragStartX: number | null = null;

  const onMouseMove = (e: Event) => {
    if (!isDragging) return;
    setPanelWidth(Math.max(MIN_PANEL_WIDTH, Math.min(EXPANDED_PANEL_WIDTH, (e as MouseEvent).clientX)));
  };
  const onMouseUp = (e: Event) => {
    if (!isDragging) return;
    isDragging = false;
    if (dragStartX !== null) {
      const moved = Math.abs((e as MouseEvent).clientX - dragStartX) > 5;
      setPanelWidth((w) => snapPanelWidth(w, moved));
      dragStartX = null;
    }
  };
  const onMouseDown = (x: number) => {
    isDragging = true;
    dragStartX = x;
  };
  return { onMouseMove, onMouseUp, onMouseDown };
}

/** Manages the resizable panel width, the shared mapRef, and global drag listeners. */
export const usePanelResize = () => {
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const mapRef = useRef<MapRef>(null);
  const dragHandlers = useRef(createDragHandlers(setPanelWidth));

  // Resize the map canvas whenever the panel width or collapsed state changes
  useEffect(() => {
    mapRef.current?.resize();
  }, [panelWidth, isPanelCollapsed]);

  // Attach global mouse listeners for drag-to-resize
  useEffect(() => {
    const { onMouseMove, onMouseUp } = dragHandlers.current;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleDragHandleMouseDown = (e: { clientX: number }) => {
    // While collapsed, the handle only re-expands the panel; it doesn't start a resize drag.
    if (isPanelCollapsed) {
      setIsPanelCollapsed(false);
      return;
    }
    dragHandlers.current.onMouseDown(e.clientX);
  };

  return {
    panelWidth,
    mapRef,
    mapLoaded,
    setMapLoaded,
    handleDragHandleMouseDown,
    isPanelCollapsed,
    setIsPanelCollapsed,
  };
};
